import { STEP } from "#src/constants/step.ts";
import {
  type DataRequirement,
  type DataRequirements,
  REQS,
} from "#src/constants/data-requirements.ts";
import { getCoverageCarriers } from "#src/services/http-coverage.service.ts";
import {
  defineStore,
  generateInitialState,
  resetState,
  type ToSetupState,
} from "#src/stores/state-wrapper.ts";

import { useStepPhysicianStore } from "#src/stores/step-physician.ts";
import { useEappStore } from "#src/stores/electronic-application.ts";
import { usePrimaryInsuredStore } from "#src/stores/insured.ts";
import { useStepPersonalStore } from "#src/stores/step-personal.ts";
import { type Pinia, type StoreGeneric } from "pinia";
import { computed } from "vue";
import {
  type BaseStepStoreState,
  type BaseStepStore,
} from "#src/types/StepStore.ts";

interface ExistingCoverageStep
  extends BaseStepStore,
    ToSetupState<ExistingCoverageState> {
  setStepRequirements(srq: DataRequirements): void;
  getCarriers(): Promise<unknown>;
}

function initializeRequiredFields(reqs?: DataRequirements) {
  const required = (requirement: DataRequirement): boolean =>
    reqs?.[requirement] || false;
  return {
    soldBySameAgent: required(REQS.EXISTING_COVERAGE_SOLD_BY_SAME_AGENT),
    partyNames: required(REQS.EXISTING_COVERAGE_PARTY_NAMES),
  };
}

interface ExistingCoverageState extends BaseStepStoreState {
  carriers: {
    value: number | string;
    title: string;
  }[];
  gettingCarriers: boolean;
  requiredFields: Record<string, boolean>;
}

function initialState(): ExistingCoverageState {
  return {
    inactive: false,
    requiredFields: initializeRequiredFields(),
    carriers: [],
    gettingCarriers: false,
  };
}

function initializeExistingCoverageStep(pinia: Pinia): ExistingCoverageStep {
  const state = generateInitialState<ExistingCoverageState>(initialState());

  function $reset() {
    resetState<ExistingCoverageState>(state, initialState());
  }

  const step = computed(() => STEP.EXISTING_COVERAGE);
  const previous = computed(() => {
    const personal = useStepPersonalStore(pinia);
    if (personal.inactive) return personal.previous;
    return personal.step;
  });
  const next = computed(() => {
    const physician = useStepPhysicianStore(pinia);
    if (physician.inactive) return physician.next;
    return physician.step;
  });
  const skippable = computed(() => {
    const eApp = useEappStore(pinia);
    if (eApp.isDualInsured) {
      return !eApp.existing_coverages.length;
    }
    const insured = usePrimaryInsuredStore(pinia);
    return !eApp.existingCoveragesByInsuredId[insured.id]?.length;
  });

  function setStepRequirements(srq: DataRequirements) {
    state.inactive.value = !srq[REQS.EXISTING_COVERAGE];
    state.requiredFields.value = initializeRequiredFields(srq);
  }
  async function getCarriers() {
    try {
      state.gettingCarriers.value = true;
      state.carriers.value = await getCoverageCarriers(pinia);
      state.gettingCarriers.value = false;
    } catch (e) {
      state.gettingCarriers.value = false;
      throw e;
    }
  }

  return {
    $reset,
    ...state,
    step,
    previous,
    next,
    skippable,
    setStepRequirements,
    getCarriers,
  };
}

export const useStepExistingCoverageStore = (
  pinia: Pinia,
  hot?: StoreGeneric
) => {
  return defineStore<string, ExistingCoverageStep>(
    "step-existing-coverage",
    () => {
      return initializeExistingCoverageStep(pinia);
    }
  )(pinia, hot);
};
